/**
 * Wrapper for ws client.
 *
 * Use createClient method to create instance.
 *
 * Use client.onopen = () => {} to set callback for onopen event and e.g. subscribe to channels.
 */
class WebsocketClient {
    private url: string;
    private socket: WebSocket | null;
    private afterConnect: boolean;
    private channels: Set<string>;

    public onconnecting: (() => void) | null;
    public onmessage: ((event: MessageEvent) => void) | null;
    public onopen: (() => void) | null;
    public onclose: (() => void) | null;
    public onerror: ((event: Event) => void) | null;

    constructor(url: string) {
        this.url = url;
        this.socket = null;
        this.afterConnect = false;

        this.onconnecting = null;
        this.onopen = null;
        this.onmessage = null;
        this.onclose = null;
        this.onerror = null;
        this.channels = new Set<string>();
    }

    /**
     * Initiates the connection and subscribes to provided channel.
     */
    connect(channel: string | null): void {
        if (this.afterConnect) {
            return;
        }

        if (this.onconnecting) {
            this.onconnecting();
        }

        this.socket = new WebSocket(this.url);

        this.afterConnect = true;

        //We can connect without subscribing if channel not provided.
        if (channel) {
            this.channels.add(channel);
        }

        this.socket.onopen = this.onOpen.bind(this);
        this.socket.onmessage = this.onMessage.bind(this);
        this.socket.onclose = this.onClose.bind(this);
        this.socket.onerror = this.onError.bind(this);
    }

    joinChannel(channel: string): void {
        if (this.channels.has(channel)) {
            return;
        }

        this.channels.add(channel);
        this.send({}, channel);
    }

    leaveChannel(channel: string): void {
        this.channels.delete(channel);
        this.send(
            {
                leaveChannel: true,
            },
            channel,
        );
    }

    getChannelsSet(): Set<string> {
        return this.channels;
    }

    onOpen(): void {
        //Subscribe to all channels
        this.channels.forEach(channel => {
            this.send({}, channel);
        });

        //Now handle on open bound callback. It happens after initial subscription to channel to avoid duplication subscription message via joinChannel and onOpen callback.
        if (this.onopen) {
            this.onopen();
        }
    }

    onMessage(event: MessageEvent): void {
        if (this.onmessage) {
            this.onmessage(event);
        }
    }

    onClose(): void {
        if (this.onclose) {
            this.onclose();
        }

        setTimeout(() => {
            this.afterConnect = false;
            //Let's not subscribe to new channel, only reconnect to the same ones currently subscribed under this.channels.
            this.connect(null);
        }, 1000);
    }

    onError(event: Event): void {
        console.error("Websocket error", event);
        if (this.onerror) {
            this.onerror(event);
        }
    }

    /**
     * Sends message to target channel or to first subscribed.
     * This is useful in scoreboards, since only they emit events. Thus scoreboards subscribe to only one channel.
     */
    send(messageObj: Record<string, unknown>, targetChannel: string | null = null): void {
        messageObj.channel = targetChannel ?? this.channels.values().next().value;

        this.socket!.send(JSON.stringify(messageObj));
    }

    close(): void {
        this.socket!.close();
    }
}

export type WebsocketClientType = ReturnType<typeof createClient>;

export function createClient(): WebsocketClient {
    const url = `wss://${window.location.host}/_wss`;

    return new WebsocketClient(url);
}
